// src/components/Auth/Signup.js
import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Lock, Mail, Phone, User, UserPlus } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAuthApi } from "../../hooks/useAuthApi";
import {
  signupDefaultValues,
  signupSchema,
} from "../../utils/validationSchemas";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useToast } from "../../hooks/use-toast"; // Fixed import path

const Signup = ({ onToggle, loading, setLoading }) => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { toast } = useToast(); // Make sure this hook is working
  const {
    register: registerUser,
    loading: apiLoading,
    error: apiError,
  } = useAuthApi();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    watch,
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: signupDefaultValues,
  });

  // Watch password field for confirm password validation
  const password = watch("password");
  const agreeToDisclaimer = watch("agreeToDisclaimer");

  // Clear API errors when form changes
  React.useEffect(() => {
    if (apiError) {
      clearErrors("root");
    }
  }, [apiError, clearErrors]);

  const onSubmit = async (formData) => {
    setLoading(true);

    try {
      const apiData = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        repassword: formData.confirmPassword,
        phone: formData.phone,
      };

      const response = await registerUser(apiData);

      // Check for success based on your API response structure
      if (response?.status === 201 || response?.success) {
        // Show success toast
        toast({
          title: "Account created successfully!",
          description:
            response.data?.message ||
            "Welcome to GEETH365. Your account has been created.",
          duration: 3000,
        });

        console.log("Toast should have been shown"); // Debug log

        // Navigate to home page after a short delay
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        // Handle unexpected response
        setError("root", {
          type: "server",
          message: "Registration completed but unexpected response.",
        });
      }
    } catch (error) {
      console.error("Registration error:", error); // Debug log

      // Handle specific error cases
      if (error.response?.data?.errors) {
        // Handle field-specific errors from API
        const fieldErrors = error.response.data.errors;
        Object.keys(fieldErrors).forEach((field) => {
          setError(field, {
            type: "server",
            message: fieldErrors[field][0],
          });
        });
      } else if (error.response?.data?.message) {
        setError("root", {
          type: "server",
          message: error.response.data.message,
        });
      } else {
        setError("root", {
          type: "server",
          message: "Registration failed. Please try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Prevent non-numeric input and limit to 10 digits for phone field
  const handlePhoneInput = (e) => {
    let value = e.target.value.replace(/[^\d]/g, "");
    // Limit to 10 digits
    if (value.length > 10) {
      value = value.slice(0, 10);
    }
    e.target.value = value;
  };

  const isLoading = loading || apiLoading;

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* ... rest of your form JSX remains the same ... */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label
              htmlFor="signup-first-name"
              className="flex items-center space-x-2"
            >
              <User className="w-4 h-4" />
              <span>First Name</span>
            </Label>
            <Input
              id="signup-first-name"
              type="text"
              placeholder="Enter first name"
              className="mt-1"
              {...register("first_name")}
              disabled={isLoading}
            />
            {errors.first_name && (
              <p className="text-red-500 text-sm mt-1">
                {errors.first_name.message}
              </p>
            )}
          </div>

          <div>
            <Label
              htmlFor="signup-last-name"
              className="flex items-center space-x-2"
            >
              <User className="w-4 h-4" />
              <span>Last Name</span>
            </Label>
            <Input
              id="signup-last-name"
              type="text"
              placeholder="Enter last name"
              className="mt-1"
              {...register("last_name")}
              disabled={isLoading}
            />
            {errors.last_name && (
              <p className="text-red-500 text-sm mt-1">
                {errors.last_name.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="signup-email" className="flex items-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>Email</span>
          </Label>
          <Input
            id="signup-email"
            type="email"
            placeholder="Enter your email"
            className="mt-1"
            {...register("email")}
            disabled={isLoading}
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="signup-phone" className="flex items-center space-x-2">
            <Phone className="w-4 h-4" />
            <span>Phone Number</span>
          </Label>
          <Input
            id="signup-phone"
            type="tel"
            placeholder="Enter 10-digit phone number"
            className="mt-1"
            {...register("phone")}
            onInput={handlePhoneInput}
            inputMode="numeric"
            maxLength={10}
            disabled={isLoading}
          />
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
          )}
          {!errors.phone && (
            <p className="text-gray-500 text-xs mt-1">
              Enter 10-digit number only
            </p>
          )}
        </div>

        <div>
          <Label
            htmlFor="signup-password"
            className="flex items-center space-x-2"
          >
            <Lock className="w-4 h-4" />
            <span>Password</span>
          </Label>
          <Input
            id="signup-password"
            type="password"
            placeholder="Create a password (min 8 characters)"
            className="mt-1"
            {...register("password")}
            disabled={isLoading}
          />
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">
              {errors.password.message}
            </p>
          )}
        </div>

        <div>
          <Label
            htmlFor="signup-confirm-password"
            className="flex items-center space-x-2"
          >
            <Lock className="w-4 h-4" />
            <span>Confirm Password</span>
          </Label>
          <Input
            id="signup-confirm-password"
            type="password"
            placeholder="Confirm your password"
            className="mt-1"
            {...register("confirmPassword")}
            disabled={isLoading}
          />
          {errors.confirmPassword && (
            <p className="text-red-500 text-sm mt-1">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        {/* Disclaimer Checkbox */}
        <div className="space-y-3">
          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 transition-colors hover:bg-gray-100">
            <div className="flex items-center h-5 mt-0.5">
              <input
                type="checkbox"
                id="agreeToDisclaimer"
                className="w-4 h-4 text-orange-600 bg-white border-gray-300 rounded focus:ring-orange-500 focus:ring-2 cursor-pointer"
                {...register("agreeToDisclaimer")}
                disabled={isLoading}
              />
            </div>
            <div className="flex-1">
              <Label
                htmlFor="agreeToDisclaimer"
                className="text-sm font-medium text-gray-900 cursor-pointer select-none"
              >
                I agree to the disclaimer
              </Label>
              <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                I understand that the medicines, lab tests, and doctor
                consultations provided through GEETH365 are supervised and
                delivered by qualified expert doctors, pharmacists, and
                healthcare professionals from our certified retail partners.
                GEETH365 acts as a platform connecting users with these verified
                healthcare providers.
              </p>
            </div>
          </div>
          {errors.agreeToDisclaimer && (
            <p className="text-red-500 text-sm mt-1">
              {errors.agreeToDisclaimer.message}
            </p>
          )}
        </div>

        {(errors.root || apiError) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">
              {errors.root?.message || apiError}
            </p>
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2.5"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Creating Account...
            </>
          ) : (
            <>
              <UserPlus className="w-4 h-4 mr-2" />
              Sign Up Free
            </>
          )}
        </Button>
      </form>

      <div className="text-center">
        <p className="text-sm text-gray-600">
          Already have an account?{" "}
          <button
            type="button"
            onClick={() => onToggle("login")}
            className="text-orange-600 hover:text-orange-700 font-medium"
            disabled={isLoading}
          >
            Login
          </button>
        </p>
      </div>
    </div>
  );
};

export default Signup;
